import { redirect, type LoaderFunctionArgs } from '@remix-run/node'
import { Link, useNavigate, useParams } from '@remix-run/react'
import {
	Tabs,
	TabsList,
	TabsTrigger,
	TabsContent,
} from '#app/components/ui/tabs.tsx'
import { defaultSource, type FaqSource, faqUrl, sources } from './faq.constants'

export function loader({ params }: LoaderFunctionArgs) {
	let source = params.source ?? ''

	if (!sources.map((s) => s.key).includes(source)) {
		return redirect(faqUrl(defaultSource))
	}

	return {}
}

export default function FaqRoute() {
	let params = useParams()
	let source = params.source as FaqSource
	let navigate = useNavigate()

	return (
		<div className="container grid">
			<div className="mx-auto py-20">
				<Tabs
					value={source}
					className="max-w-prose"
					onValueChange={(nextSource) => {
						navigate(faqUrl(nextSource as FaqSource), { replace: true })
					}}
				>
					<TabsList className="mx-auto flex gap-x-4">
						{sources.map((s) => {
							return (
								<TabsTrigger
									key={s.key}
									value={s.key}
									className="flex-shrink-0 rounded-full px-2 hover:bg-primary/15 has-[.active]:bg-primary/25 has-[.active]:text-primary-dark data-[state=active]:bg-primary/25 data-[state=active]:text-primary-dark"
								>
									{s.title}
								</TabsTrigger>
							)
						})}
					</TabsList>
					<TabsContent value="parents-and-students">
						<dl className="flex flex-col gap-4">
							<div>
								<Question>
									Are all tremolo instructors planning on pursuing music after
									college?
								</Question>
								<Answer>
									Many of our instructors are majoring or minoring in music or
									their instrument of choice, but others are not. If this is a
									factor that is important to you and your student, it can be
									prioritized. Some students prefer mentors with other similar
									shared interests, like astronomy or engineering. Regardless,
									all of our instructors have years of experience and are more
									than capable of teaching music and passing on their passion to
									music students.
								</Answer>
							</div>

							<div>
								<Question>
									What are <Jottingz />? How can I access them?
								</Question>
								<Answer>
									Right now, <Jottingz /> are an idea. An idea that will grow as
									Tremolo does. Put simply, <Jottingz /> are notes left by the
									instructor that both student and parents have access to to
									provide feedback and reminders for what to work on between
									lessons. Many instructors include them for free, and some ask
									for a small amount of additional compensation.
								</Answer>
							</div>

							<div>
								<Question>How do I book a lesson?</Question>
								<Answer>
									Tremolo has not officially launched yet (
									<i>
										join our{' '}
										<Link
											to="/mailing-list"
											className="text-primary-dark focus-within:text-primary hover:text-primary"
										>
											mail list
										</Link>{' '}
										to be the first to hear from us when we do
									</i>
									) but all appointments will be booked through our{' '}
									<Link className="underline" to="/instructors">
										instructors
									</Link>{' '}
									page where you will have a plethora of mentors to choose from,
									with varying skill levels and availabilities, face to face or
									online. Currently, it is organized as a directory. Once
									Tremolo gains momentum, this portal will become a booking
									site.
								</Answer>
							</div>

							<div>
								<Question>
									Can I customize my service / lesson package?
								</Question>
								<Answer>
									Instructors can offer add ons to their services, such as
									transportation (they'll come to you) or <Jottingz />. Many are
									also happy to provide bulk lesson deals, and will have
									information for personal promotions provided in their
									listings.
								</Answer>
							</div>

							<div>
								<Question>
									What qualifications do Tremolo instructors have?
								</Question>
								<Answer>
									To become a Tremolo partner, instructors must prove college/
									university enrollment, provide a letter of recommendation from
									someone they have studied under or preformed with in the music
									world, pass a background check, and submit a short lesson
									introductory video. Many are majoring or minoring in music,
									but even more have 8+ years of experience with their
									instruments. All qualifications are clearly displayed on
									individual listings, so students can pick their mentors
									according to what they value most.
								</Answer>
							</div>

							<div>
								<Question>What is pricing like?</Question>
								<Answer>
									<ul className="grid list-inside list-disc gap-y-2">
										<li>
											<span className="font-bold">Like any other service</span>,
											lesson pricing with Tremolo will have a range, which we do
											not cap. Minimum lesson cost is $25.00 / hour to account
											for the years of skill and dedication that go into
											learning an instrument and minimum wage in Seattle. Many
											instructors will run reduced lesson pricing promotionals
											for your first lesson, or if you purchase lesson packages.
										</li>
										<li>
											<span className="font-bold">
												Unlike any other service
											</span>
											, Tremolo is upfront about all of your options. Browse
											your choices and weigh your values while observing what
											different instructors can provide. Honesty and upfront
											pricing are extremely important to us, whether you value
											years of experience, niche availability, or cost.
										</li>
									</ul>
								</Answer>
							</div>

							<div>
								<Question>Where can lessons take place?</Question>
								<Answer>
									Tremolo instructors offer lessons in person and online,
									depending on the needs of students. This availability may vary
									from teacher to teacher. Some instructors may be willing to
									travel to you if preferred, but ultimately lesson location is
									worked out between teacher and student/parents.
								</Answer>
							</div>
						</dl>
					</TabsContent>

					<TabsContent value="teachers">
						<dl className="flex flex-col gap-4">
							<div>
								<Question>
									Are there any upfront costs to using Tremolo?
								</Question>
								<Answer>
									Tremolo is a completely free platform to begin using. The
									instructor application process is on us ;). Once you become a
									certified instructor and we help you get your listing set up,
									we'll take 20% of each lesson in exchange for advertising for
									you, keeping our site fresh, handling payment, and the{' '}
									<Link className="underline" to="#benefits">
										other benefits
									</Link>{' '}
									you'll receive.
								</Answer>
							</div>

							<div>
								<Question>
									How can I provide user feedback / who can I contact to learn
									more about teaching and the status of my application?
								</Question>
								<Answer>
									So glad you're interested! You can email us at
									admin@tremoloinstitute.com, submit a contact request in our{' '}
									{/* TODO: change this to an internal link to "/contact" once we have that wired up */}
									<Link
										to="/mailing-list"
										className="text-primary-dark focus-within:text-primary hover:text-primary"
									>
										contact form
									</Link>
									, or casually DM us on instagram at (
									<a
										href="https://instagram.com/tremolo.institute"
										target="_blank"
										rel=""
										className="text-primary-dark underline focus-within:text-primary hover:text-primary"
									>
										<i>@tremolo.institute</i>
									</a>
									). We'd love to hear from you and will get back to you ASAP,
									wherever you're coming from.
								</Answer>
							</div>

							<div id="benefits">
								<Question>
									What company resources will I have access to as an instructor?
								</Question>
								<Answer>
									<p>
										Once you're an official Tremolo partner you will receive:
									</p>
									<ul className="grid list-inside list-disc gap-y-2">
										<li>Invitations to social and music networking events</li>
										<li>Access to Tremolo member directory</li>
										<li>Free professional photos</li>
										<li>Networking on instructor-only Slack community</li>
										<li>Lesson planning resources</li>
										<li>Pricing guide resources</li>
										<li>Personal brand development guide</li>
										<li>Secured lesson payments (down the road)</li>
										<li>
											Opportunity to present a musical portfolio to potential
											clientele.
										</li>
										<li>
											A platform for <i>your</i> business
										</li>
									</ul>
								</Answer>
							</div>

							<div>
								<Question>
									Does Tremolo provide standardized curriculum?/ what resources
									are available for lesson planning and materials?
								</Question>
								<Answer>
									Tremolo does not require a standardized curriculum to be used
									among teachers. This gives individuals the freedom to work
									with students personally, towards each unique goal. This being
									said, upon being accepted as a Tremolo instructor, you will be
									granted access to helpful documents and suggestions for first
									lesson plans, growth goals, and checkpoints students should
									ideally be meeting. These are meant to give you a leg up in
									your teaching style, which you may or may not have had a lot
									of experience developing in the past.
								</Answer>
							</div>

							<div>
								<Question>How do I set my rates?</Question>
								<Answer>
									Tremolo provides a price guide to all instructors with
									information surrounding acceptable pricing, but ultimately
									pricing is up to you! As an instructor, you build your own
									brand and network through people who you can access through
									Tremolo. You will be given the option to decrease or increase
									your rates, explain why (if desired), and run promotions/
									deals.
								</Answer>
							</div>

							<div>
								<Question>Where can lessons take place?</Question>
								<Answer>
									Tremolo instructors offer lessons in person and online,
									depending on the needs of students. This availability may vary
									from teacher to teacher. Some instructors may be willing to
									travel to you if preferred, but ultimately lesson location is
									worked out between teacher and student/parents.
								</Answer>
							</div>
						</dl>
					</TabsContent>
				</Tabs>
			</div>
		</div>
	)
}

function Jottingz() {
	return (
		<span className="bg-gradient-to-b from-primary via-primary-dark to-primary-dark bg-clip-text text-transparent">
			Jottingz
		</span>
	)
}

function Question({ children }: React.ComponentPropsWithoutRef<'dt'>) {
	return <dt className="font-bold">{children}</dt>
}

function Answer({ children }: React.ComponentPropsWithoutRef<'dd'>) {
	return <dd className="ml-2">{children}</dd>
}
